<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="edit" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Edit User</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-outline-dark me-2"
            @click="$router.push('/users')"
          >
            <vue-feather type="arrow-left" size="18" class="me-1"></vue-feather>
            Back to Users
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- User Edit Form -->
          <div v-if="!loading" class="p-2">
            <form @submit.prevent="updateUser">
              <div class="row g-4">
                <!-- User Information Section -->
                <div class="col-12">
                  <div class="d-flex align-items-center mb-3">
                    <div class="bg-primary-light p-2 rounded-2 me-2">
                      <vue-feather type="user" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="18"></vue-feather>
                    </div>
                    <h5 class="mb-0 fw-bold">User Information</h5>
                  </div>

                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">First Name</label>
                      <input type="text" class="form-control" placeholder="Enter first name" v-model="user.first_name" required/>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Last Name</label>
                      <input type="text" class="form-control" placeholder="Enter last name" v-model="user.last_name" required/>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Email</label>
                      <input type="email" class="form-control" placeholder="Enter email address" v-model="user.email" required/>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Phone</label>
                      <input type="text" class="form-control" placeholder="Enter phone number" v-model="user.phone" required/>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Role</label>
                      <select class="form-select custom-select" v-model="user.role" @change="handleRoleChange" data="">
                        <option value="" disabled selected>Select role</option>
                        <option value="primary">Primary</option>
                        <option value="admin">Admin</option>
                        <option value="finance">Finance</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Status</label>
                      <select class="form-select form-control-lg" v-model="user.status" data="">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Role Information Section -->
                <div class="col-12">
                  <div class="role-card">
                    <!-- Primary Role -->
                    <div v-if="user.role === 'primary'" class="role-info-card">
                      <div class="role-header">
                        <vue-feather type="shield" size="20" class="role-icon" stroke="#652aa3"></vue-feather>
                        <h5 class="role-title">Primary</h5>
                      </div>
                      <div class="role-body">
                        <ul class="role-permissions">
                          <li>Has all permissions</li>
                          <li>Can access all modules and pages</li>
                          <li>Can create, update, and remove data in all modules</li>
                          <li>Can manage users, roles, and permissions</li>
                          <li>Can approve the final payroll</li>
                        </ul>
                      </div>
                    </div>

                    <!-- Admin Role -->
                    <div v-if="user.role === 'admin'" class="role-info-card">
                      <div class="role-header">
                        <vue-feather type="user-check" size="20" class="role-icon" stroke="#652aa3"></vue-feather>
                        <h5 class="role-title">Admin</h5>
                      </div>
                      <div class="role-body">
                        <ul class="role-permissions">
                          <li>Has most permissions except for some super-admin specific ones</li>
                          <li>Can access most modules and pages</li>
                          <li>Can create, update, and remove data in most modules</li>
                          <li>Can manage users and roles</li>
                          <li>Can approve the payroll</li>
                        </ul>
                      </div>
                    </div>

                    <!-- Finance Role -->
                    <div v-if="user.role === 'finance'" class="role-info-card">
                      <div class="role-header">
                        <vue-feather type="dollar-sign" size="20" class="role-icon" stroke="#652aa3"></vue-feather>
                        <h5 class="role-title">Finance</h5>
                      </div>
                      <div class="role-body">
                        <ul class="role-permissions">
                          <li>Has permissions related to payroll management</li>
                          <li>Can view, create, edit, delete, and approve payrolls</li>
                          <li>Limited access to other modules</li>
                        </ul>
                      </div>
                    </div>

                    <!-- User Role -->
                    <div v-if="user.role === 'user'" class="role-info-card">
                      <div class="role-header">
                        <vue-feather type="user" size="20" class="role-icon" stroke="#652aa3"></vue-feather>
                        <h5 class="role-title">User</h5>
                      </div>
                      <div class="role-body">
                        <ul class="role-permissions">
                          <li>Has basic permissions for viewing and creating leave requests</li>
                          <li>Can view own profile and payslips</li>
                          <li>Can create and view own leave requests</li>
                          <li>Limited access to other modules</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex justify-content-end mt-4">
                <button type="button" class="btn btn-outline-dark me-2" @click="$router.push('/users')">
                  <vue-feather type="x" size="16" class="me-1"></vue-feather>
                  Back
                </button>
                <button type="submit" class="btn btn-primary">
                  <vue-feather type="save" size="16" class="me-1"></vue-feather>
                  Update User
                </button>
              </div>
            </form>
          </div>

          <!-- Permissions Edit Form -->
          <div v-if="!loading" class="p-2">
            <form @submit.prevent="updatePermissions">
              <div class="row g-4">
                <!-- User Permissions Section -->
                <div class="col-12">
                  <div class="d-flex align-items-center mb-3">
                    <div class="bg-primary-light p-2 rounded-2 me-2">
                      <vue-feather type="lock" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="18"></vue-feather>
                    </div>
                    <h5 class="mb-0 fw-bold">User Permissions</h5>
                  </div>

                  <!-- Permissions explanation -->
                  <div v-if="rolePermissions.length > 0" class="alert alert-info mb-3">
                    <div class="d-flex">
                      <vue-feather type="info" class="me-2" size="18"></vue-feather>
                      <div>
                        <strong>Note:</strong> Permissions marked with <span class="badge bg-dark">{{ user.role }}</span> are included in the user's role and cannot be modified individually.
                        To change these permissions, you need to assign a different role to the user.
                      </div>
                    </div>
                  </div>

                  <!-- Permissions Categories -->
                  <div class="permissions-container">
                    <div v-for="(permissions, category) in categorizedPermissions" :key="category" class="permission-category mb-4">
                      <div class="category-header mb-2">
                        <h6 class="fw-bold text-primary">{{ category }}</h6>
                      </div>

                      <div class="permissions-grid">
                        <div
                          v-for="permission in permissions"
                          :key="permission.id"
                          class="permission-item"
                          @click="togglePermission(permission)"
                          :style="{ cursor: rolePermissions.includes(permission.name) ? 'not-allowed' : 'pointer' }"
                        >
                          <div class="form-check">
                            <input
                              type="checkbox"
                              class="form-check-input"
                              :id="'permission-' + permission.id"
                              :value="permission.name"
                              v-model="user.permissionNames"
                              :disabled="rolePermissions.includes(permission.name)"
                              :title="rolePermissions.includes(permission.name) ? 'This permission is included in the user\'s role and cannot be modified' : ''"
                            >
                            <label
                              class="form-check-label text-black fs-6"
                              :for="'permission-' + permission.id"
                              @click="togglePermission(permission)"
                              :class="{'text-muted': rolePermissions.includes(permission.name)}"
                              :title="rolePermissions.includes(permission.name) ? 'This permission is included in the user\'s role and cannot be modified' : ''"
                            >
                              {{ permission.display_name }}
                              <span v-if="rolePermissions.includes(permission.name)" class="ms-1 badge bg-dark">{{ user.role }}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <div class="d-flex justify-content-end mt-4">
                    <button type="button" class="btn btn-outline-dark me-2" @click="$router.push('/users')">
                      <vue-feather type="x" size="16" class="me-1"></vue-feather>
                      Back
                    </button>
                    <button type="submit" class="btn btn-primary">
                      <vue-feather type="save" size="16" class="me-1"></vue-feather>
                      Update Permissions
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>


        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import userService from "@/api/userService";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';

export default {
  name: "UserEdit",
  components: { VueFeather },

  data() {
    return {
      profile: {},
      loading: true,
      roles: [],
      permissions: [],
      categorizedPermissions: {},
      originalPermissions: [],
      rolePermissions: [],
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        status: '',
        permissionNames: []
      },
    }
  },

  methods: {
    async getUser() {
      try {
        this.loading = true;
        this.user = await userService.getUser(this.$route.params.id);
        this.originalPermissions = [...this.user.permissionNames];
        this.roles = await userService.getRoles();
        this.permissions = await userService.getPermissions();
        this.categorizePermissions();

        // Initialize rolePermissions based on the user's current role
        if (this.user.role) {
          const currentRole = this.roles.find(role => role.name === this.user.role);
          if (currentRole && currentRole.permissions) {
            if (Array.isArray(currentRole.permissions)) {
              // If permissions is an array of objects with name property
              if (currentRole.permissions.length > 0 && typeof currentRole.permissions[0] === 'object' && currentRole.permissions[0].name) {
                this.rolePermissions = currentRole.permissions.map(p => p.name);
              }
              // If permissions is already an array of strings
              else if (currentRole.permissions.length > 0 && typeof currentRole.permissions[0] === 'string') {
                this.rolePermissions = [...currentRole.permissions];
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        this.loading = false;
      }
    },

    async updateUser() {
      try {
        this.loading = true;
        const response = await userService.updateUser(this.user, this.user.id);
        if (response) {
          this.$router.push('/users');
        }
      } catch (error) {
        console.error("Error updating user:", error);
      } finally {
        this.loading = false;
      }
    },

    categorizePermissions() {
      // Group permissions by category
      this.categorizedPermissions = {};

      if (!this.permissions || this.permissions.length === 0) {
        return;
      }

      this.permissions.forEach(permission => {
        // If permission doesn't have a category, assign it to "Other"
        const category = permission.category || "Other";

        if (!this.categorizedPermissions[category]) {
          this.categorizedPermissions[category] = [];
        }
        this.categorizedPermissions[category].push(permission);
      });
    },

    handleRoleChange() {
      // When role changes, update permissions based on the selected role
      if (!this.user.role) {
        this.user.permissionNames = [];
        this.rolePermissions = [];
        return;
      }

      const selectedRole = this.roles.find(role => role.name === this.user.role);

      if (!selectedRole) {
        console.warn(`Role '${this.user.role}' not found in roles array`);
        return;
      }

      // Store the role permissions to identify which ones should be disabled
      this.rolePermissions = [];

      // Check if the role has permissions
      if (selectedRole.permissions && Array.isArray(selectedRole.permissions)) {
        // If permissions is an array of objects with name property
        if (selectedRole.permissions.length > 0 && typeof selectedRole.permissions[0] === 'object' && selectedRole.permissions[0].name) {
          this.rolePermissions = selectedRole.permissions.map(p => p.name);
          this.user.permissionNames = [...this.rolePermissions];
        }
        // If permissions is already an array of strings
        else if (selectedRole.permissions.length > 0 && typeof selectedRole.permissions[0] === 'string') {
          this.rolePermissions = [...selectedRole.permissions];
          this.user.permissionNames = [...this.rolePermissions];
        }
        // Empty array case
        else {
          this.rolePermissions = [];
          this.user.permissionNames = [];
        }
      } else {
        // If no permissions defined for the role, clear permissions
        this.rolePermissions = [];
        this.user.permissionNames = [];
      }
    },

    async updatePermissions() {
      const added = this.user.permissionNames.filter(permission => !this.originalPermissions.includes(permission));
      const removed = this.originalPermissions.filter(permission => !this.user.permissionNames.includes(permission));

      let data = {
        user_id: this.user.id,
        added: added,
        removed: removed
      }
      await userService.updatePermissions(data)
    },

    togglePermission(permission) {
      // Skip if the permission is included in the role (disabled)
      if (this.rolePermissions.includes(permission.name)) {
        return;
      }

      // Toggle the permission in the user's permissionNames array
      const index = this.user.permissionNames.indexOf(permission.name);
      if (index === -1) {
        // Permission not found, add it
        this.user.permissionNames.push(permission.name);
      } else {
        // Permission found, remove it
        this.user.permissionNames.splice(index, 1);
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.getUser();
  },

  watch: {
    // Watch for role changes to update permissions
    'user.role': function(newRole, oldRole) {
      if (newRole !== oldRole) {
        this.handleRoleChange();
      }
    }
  }
}
</script>

<style scoped>
/* Base styles */
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

/* Button styles */
.btn {
  transition: all 0.3s ease;
  border-radius: 8px;
  font-weight: 500;
  padding: 0.5rem 1.25rem;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background-color: #652aa3;
  border-color: #652aa3;
}

.btn-primary:hover {
  background-color: #5a25a0;
  border-color: #5a25a0;
}

/* Form control styles */
.form-control, .form-select {
  border-radius: 8px;
  padding: 0.6rem 1rem;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.form-control:focus, .form-select:focus {
  border-color: #652aa3;
  box-shadow: 0 0 0 0.25rem rgba(101, 42, 163, 0.25);
}

.custom-select {
  background-position: right 0.75rem center;
  background-size: 16px 12px;
}

/* Card styles */
.card {
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
}

.card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
}

/* Role card styles */
.role-card {
  margin-top: 1rem;
}

.role-info-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  animation: fadeIn 0.5s ease;
}

.role-info-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.role-header {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  background-color: rgba(101, 42, 163, 0.05);
  border-bottom: 1px solid rgba(101, 42, 163, 0.1);
}

.role-icon {
  margin-right: 0.75rem;
  background-color: rgba(101, 42, 163, 0.1);
  padding: 0.5rem;
  border-radius: 8px;
}

.role-title {
  margin: 0;
  font-weight: 600;
  color: #652aa3;
  font-size: 1.1rem;
}

.role-body {
  padding: 1.25rem;
}

.role-permissions {
  list-style-type: none;
  padding-left: 0.5rem;
  margin-bottom: 0;
}

.role-permissions li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.role-permissions li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #652aa3;
  font-weight: bold;
}

.role-permissions li:last-child {
  margin-bottom: 0;
}

/* Permissions styles */
.permissions-container {
  margin-top: 1.5rem;
}

.permission-category {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.permission-category:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.category-header {
  border-bottom: 1px solid rgba(101, 42, 163, 0.1);
  padding-bottom: 0.75rem;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.permission-item {
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.permission-item:hover {
  background-color: rgba(83, 24, 145, 0.1);
}

.form-check-input:checked {
  background-color: #652aa3;
  border-color: #652aa3;
}

.form-check-label {
  cursor: pointer;
  font-size: 0.9rem;
  color: #495057;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
